import React from 'react';
import FullWidthBarBreadcrumbs from '@/components/Breadcrumb/FullWidthBarBreadcrumbs';
import CenteredContentSection from '@/components/ContentSection/CenteredContentSection';
import { H3, H4, H5 } from '@/components/Element/Heading';
import MetaTags from '@/components/Head/MetaTags';
// import SimpleCenteredHeaderSection from '@/components/HeaderSection/SimpleCenteredHeaderSection';
import Image from 'next/legacy/image';
// import SideCardContent from '@/components/SideCardContent/SideCardContent';
import SideCardCTA from '@/components/SideCardCTA/SideCardCTA2';
import Link from 'next/link';
import ImageOne from 'images/solar-guide/image51.png';
import ImageTwo from 'images/solar-guide/image13.png';
import ImageThree from 'images/solar-guide/image23.png';
import ImageFour from 'images/solar-guide/image37.png';
import FullWidthGuideFormSection from '@/components/LeadForm/FullWidthGuideForm';
import ReadingTime from '@/components/ReadingTime/ReadingTime';
import { SolarGuideTable } from './why-go-solar';
const Page = () => {
  return (
    <article>
      <MetaTags title="Solar Panels" description="2023 Solar Guide" />
      <FullWidthBarBreadcrumbs
        pages={[
          {
            name: 'Home Solar Guide',
            href: '/home-solar-guide',
          },
          {
            name: 'Solar Panels',
            href: '/home-solar-guide/solar-panels',
          },
        ]}
      />
      <CenteredContentSection>
        <section id="section1">
          <div className="flex justify-between items-center">
            <Link
              href={'/home-solar-guide/what-components-make-up-a-solar-system'}
            >
              ← What Components Make Up A Solar System?
            </Link>
            <Link href={'/home-solar-guide/battery-storage-products'}>
              Battery Storage Products →
            </Link>
          </div>
          <H3>Panel Manufacturers</H3>
          <ReadingTime wordCount={858} />
          <p>
            There are many solar panels available to purchase and install. Out
            of all the companies currently making solar panels the top names
            associated with the <strong>best solar panels</strong> for home use
            on the market are listed below. We encourage you to look up each of
            these solar manufacturers to read about how they present their
            panels online:
          </p>
          <div className="flex justify-between">
            <div className="w-1/2">
              <ul className="list-none">
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/lg">LG</Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/rec">REC</Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/panasonic">
                    Panasonic
                  </Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/silfab">Silfab</Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/jinko-solar-holding">
                    JinkoSolar
                  </Link>
                </li>
              </ul>
            </div>
            <div className="w-1/2">
              <ul className="list-none">
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/q-cells">
                    Q CELLS
                  </Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/mission-solar-energy">
                    Mission Solar
                  </Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/canadian-solar">
                    Canadian Solar
                  </Link>
                </li>
                <li className="py-1">
                  <Link href="/best-solar-panels-for-homes/longi">
                    Longi Solar
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <p>
            View our full{' '}
            <Link href="/best-solar-panels-for-homes">online database</Link> of
            panel manufacturers and panel types.
          </p>
          <H4>Panel Types</H4>
          <p>
            There are 4 major types of solar panels available on the market
            today: monocrystalline, polycrystalline, PERC, and thin-film panels.
            Monocrystalline solar panels are typically the only panel type sold
            in residential applications since 2018.
          </p>
          <H5>
            <strong>Monocrystalline solar panels</strong>
          </H5>
          <div className="w-72 mx-auto">
            <Image src={ImageOne} alt="Monocrystalline solar panels" />
          </div>
          <p>
            Also known as single-crystal panels, these are made from a single
            pure silicon crystal that is cut into several wafers. Since they are
            made from pure silicon they can be readily identified by their dark
            black color. The use of pure silicon with highest sunlight to
            electricity conversion efficiency makes monocrystalline panels the
            most space-efficient and longest-lasting among all solar panel
            types.
          </p>
          {/* image */}
          <H5>
            <strong>Polycrystalline solar panels</strong>
          </H5>
          <div className="w-72 mx-auto">
            <Image src={ImageTwo} alt="Polycrystalline solar panels" />
          </div>
          <p>
            As the name implies these come from different silicon crystals
            instead of one. The silicon fragments are melted and poured into a
            square mold. This makes polycrystalline cells much more affordable
            since there is hardly any wastage, and gives them that
            characteristic square shape.
          </p>
          <p>
            However, this also makes them less efficient in terms of energy
            conversion and space, since their silicon purity and construction
            are lower than monocrystalline panels. They also have lower heat
            tolerance which means they are less efficient in high-temperature
            environments.
          </p>
          <p className="italic">
            Polycrystalline solar panels are typically not used in residential
            applications anymore because of their lower efficiency and since
            monocrystalline costs have come way down over the years.
          </p>
          {/* image */}
          <H5>
            <strong>Passivated Emitter and Rear Cell (PERC) panels</strong>
          </H5>
          <div className="w-72 mx-auto">
            <Image
              src={ImageThree}
              alt="Passivated Emitter and Rear Cell (PERC) panels"
            />
          </div>
          <p>
            PERC solar panels are an improvement of the traditional
            monocrystalline cell. First introduced in 1989 new technology adds a
            passivation layer in the rear surface of the cell that enhances
            efficiency in several ways:
          </p>
          <ul>
            <li>
              It reflects light back into the cell, increasing the amount of
              solar radiation that gets absorbed.
            </li>
            <li>
              It reduces the natural tendency of electrons to recombine and
              inhibits the flow of electrons in the system.
            </li>
            <li>
              It allows greater wavelengths of light to be reflected. Light
              waves over 1,180nm can’t be absorbed by silicon wafers and simply
              pass through, so they end up heating the cell’s metal back sheet
              and reduce its efficiency. The passivation layer reflects these
              higher wavelengths and stops them from heating up the back sheet.
            </li>
          </ul>
          <p className="italic">
            PERC panels are not widely used in regular residential applications
            due to cost and availability.
          </p>
          <H5>
            <strong>Thin-film solar panels</strong>
          </H5>
          <div className="w-72 mx-auto">
            <Image src={ImageFour} alt="Thin-film solar panles" />
          </div>
          <p>
            Thin-film panels are characterized by very fine layers that are thin
            enough to be flexible. Each panel does not require a frame backing,
            making them lighter and easier to install. Unlike crystalline
            silicon panels that come in standardized sizes of 60, 72, and
            96-cell counts, thin-film panels can come in different sizes to suit
            specific needs. However, they are less efficient than typical
            silicon solar panels.
          </p>
          <p className="italic">
            Thin-film solar panels are not used often in residential
            applications due to the limited roof area and relatively low
            efficiency of thin film per unit area.
          </p>
          <H4>
            <strong>Panel Warranties</strong>
          </H4>
          <p>
            There are two types of warranties available when it comes to solar
            panels: the product warranty and the performance warranty. Both
            should be important to you. Wading through the details of different
            warranty options can be daunting and tiresome. Below we explain the
            differences in the solar warranties in an easy-to-understand way.
          </p>
          <H5>
            <strong>Product Warranty</strong>
          </H5>
          <p>
            A solar product warranty guarantees the design and construction of
            the solar panel itself. It covers manufacturing issues,
            environmental damage and early wear and tear of the solar panel.
            Usually the warranty for solar panels is 10–25 years with a few
            manufacturers offering a 30-year product warranty. The longer the
            warranty is, the better for you the homeowner.
          </p>
          <p>
            Most of the time, solar panels do not have issues, and even if one
            panel fails, the system will keep running. However, if the failure
            affects other panels or the entire system you will need to take
            advantage of the product warranty. If there&apos;s a problem while
            under warranty, the manufacturer will ship them a free replacement
            panel. In most cases you must pay for shipping and installation
            charges.
          </p>
          <p>
            Some manufacturer warranties include provisions for labor and
            shipping costs for the full term so customers have great peace of
            mind.
          </p>
          <H5>
            <strong>Performance Warranty</strong>
          </H5>
          <p>
            A solar performance warranty guarantees that the solar panel will
            not degrade at an unreasonable rate. All solar panels experience
            some decline in electricity production over time. The{' '}
            <Link href={'/solar-faq/how-long-do-solar-panels-last'}>
              normal amount of production decline
            </Link>{' '}
            is about 8% reduction in 10 years and 15% reduction in 25 years. If
            a panel declines at a higher rate than normal, the manufacturer will
            fix it at no cost or ship a free solar panel replacement.{' '}
          </p>
        </section>
      </CenteredContentSection>
      {/* <SideCardContent titles={['The Difficulty of Installation']} /> */}
      <SideCardCTA />
      <FullWidthGuideFormSection cid={'a4oqmxxt5un31ha0'} />
      <SolarGuideTable />
    </article>
  );
};

export default Page;
